import React, { useRef } from "react";
import { ContinuousScaleAnswerFormat, ScaleAnswerFormat, SurveyFormStyle } from "../../../../types/Survey";
import "../../SurveyVars.css"
import "./Scale.css"

export interface ScaleProps {
    answerFormat: ScaleAnswerFormat;
    value: string;
    onChange?: (value: string) => void;
    topBorder?: boolean;
    variant?: SurveyFormStyle
}

export default function Scale(props: ScaleProps) {
    let scaleLineRef = useRef<HTMLDivElement>(null);

    function getMaximumFractionDigits() {
        if (props.answerFormat.type == "ScaleAnswerFormat") { return 0; }
        let continousScaleAnswerFormat = props.answerFormat as ContinuousScaleAnswerFormat;
        var fractionDigits = continousScaleAnswerFormat.maximumFractionDigits || 0;
        if (fractionDigits < 0) { return 0; }
        if (fractionDigits > 4) { return 4; }
        return fractionDigits;
    }

    function formatValue(value: string) {
        if (isNaN(parseFloat(value))) {
            return null;
        }

        if (props.answerFormat.type == "ScaleAnswerFormat") {
            return parseInt(value).toFixed(0);
        }

        //extra parseFloat ensures we trim trailing 0s
        return parseFloat(parseFloat(value).toFixed(getMaximumFractionDigits())).toString();
    }

    let minimumValue = props.answerFormat.minimumValue || 0;
    let maximumValue = (!props.answerFormat.maximumValue && props.answerFormat.maximumValue != 0) ? 10 : props.answerFormat.maximumValue;
    var range = maximumValue - minimumValue;
    let step = props.answerFormat.step || 1;

    function getAnswerMarker() {
        if (props.value === "") {
            return null;
        }
        var value = parseFloat(props.value);
        var position = ((value - minimumValue) / range) * 100;

        let styles: React.CSSProperties = {};
        if (props.answerFormat.vertical) {
            styles.top = 100 - position + "%";
        } else {
            styles.left = position + "%";
        }

        return <div className="answer-marker-wrapper" style={styles}>
            <div className="answer-marker"></div>
        </div>
    }

    let ticks: number[] = [];
    if (props.answerFormat.type == "ScaleAnswerFormat") {
        for (var i = step; i < range; i += step) {
            let percentage = (i * 1.0 / range) * 100;
            ticks.push(percentage);
        }
    }

    function updateScalePosition(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        function setAnswerInPercent(percent: number) {
            var targetValue = range * percent + minimumValue;
            if (targetValue < minimumValue) {
                targetValue = minimumValue;
            }

            if (targetValue > maximumValue) {
                targetValue = maximumValue;
            }

            props.onChange?.(targetValue.toFixed(getMaximumFractionDigits()));
        }

        let boundingRect = scaleLineRef.current?.getBoundingClientRect();
        if (!boundingRect) {
            return;
        }
        if (props.answerFormat.vertical) {
            let height = boundingRect.height;
            var y = e.clientY - boundingRect.top;
            var percent = 1 - (y / height);
            setAnswerInPercent(percent);
        } else {
            let width = boundingRect.width;
            let x = e.clientX - boundingRect.left;
            let percent = x / width;
            setAnswerInPercent(percent);
        }
    }

    function getMinimumStepValue() {
        const maximumFractionDigits = getMaximumFractionDigits();
        return maximumFractionDigits ? Math.pow(10, -maximumFractionDigits) : 1;
    }

    // Helper to adjust value with bounds checking and formatting
    function adjustValue(
        adjustFn: (currentValue: number) => number,
        clampMin: number,
        clampMax: number
    ) {
        const currentValue = props.value === "" ? minimumValue : parseFloat(props.value);
        const adjusted = adjustFn(currentValue);
        const clamped = Math.max(clampMin, Math.min(adjusted, clampMax));
        return clamped.toFixed(getMaximumFractionDigits());
    }

    function roundToFractionalDigits(number: number) {
        const digits = getMaximumFractionDigits();
        return Math.round(number * Math.pow(10, digits)) / Math.pow(10, digits);
    }

    function adjustByHalfToMax() {
        return adjustValue(
            (currentValue) => {
                const absoluteValue = Math.abs(currentValue - maximumValue);
                if (roundToFractionalDigits(absoluteValue) <= getMinimumStepValue()) {
                    return maximumValue;
                }
                return currentValue + ((maximumValue - currentValue) / 2);
            },
            minimumValue,
            maximumValue
        );
    }

    function adjustByHalfToMin() {
        return adjustValue(
            (currentValue) => {
                const absoluteValue = Math.abs(currentValue - minimumValue)
                if (roundToFractionalDigits(absoluteValue) <= getMinimumStepValue()) {
                    return minimumValue;
                }
                return currentValue - ((currentValue - minimumValue) / 2)
            },
            minimumValue,
            maximumValue
        );
    }

    function adjustByIncrementUp() {
        return adjustValue(
            (currentValue) => currentValue + getMinimumStepValue(),
            minimumValue,
            maximumValue
        );
    }

    function adjustByIncrementDown() {
        return adjustValue(
            (currentValue) => currentValue - getMinimumStepValue(),
            minimumValue,
            maximumValue
        );
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
            e.preventDefault();
            props.onChange?.(adjustByIncrementDown());
        } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
            e.preventDefault();
            props.onChange?.(adjustByIncrementUp());
        } else if (e.key === "Home") {
            e.preventDefault();
            props.onChange?.(minimumValue.toFixed(getMaximumFractionDigits()));
        } else if (e.key === "End") {
            e.preventDefault();
            props.onChange?.(maximumValue.toFixed(getMaximumFractionDigits()));
        } else if (e.key === "PageUp") {
            e.preventDefault();
            props.onChange?.(adjustByHalfToMax());
        } else if (e.key === "PageDown") {
            e.preventDefault();
            props.onChange?.(adjustByHalfToMin());
        }
    }

    let classes = ["scale-wrapper"];
    if (props.topBorder) {
        classes.push("scale-wrapper-top-border");
    }
    if (props.variant == "cardBased") {
        classes.push("scale-wrapper-card-based");
    }
    return <div className={classes.join(" ")}>
        <div className="scale-border">
            <div className="selected-value-display">
                {formatValue(props.value)}
            </div>
            <div className={props.answerFormat.vertical ? "scale-vertical" : "scale-horizontal"} tabIndex={0} onKeyDown={handleKeyDown}>
                <div className="value min-value">
                    {formatValue(minimumValue.toString())}
                </div>
                <div className="scale">
                    {props.answerFormat.type == "ScaleAnswerFormat" &&
                        <div className="tick first-tick" />
                    }
                    <div ref={scaleLineRef} className="scale-line-wrapper horizontal" onClick={(e) => updateScalePosition(e)}>
                        <div className="scale-line"></div>
                        <div className="click-area"></div>
                    </div>
                    {props.answerFormat.type == "ScaleAnswerFormat" &&
                        <div className="tick last-tick"></div>
                    }
                    {getAnswerMarker()}
                    {ticks.map(tick =>
                        <div key={tick} className='tick' style={{
                            top: props.answerFormat.vertical ? tick + "%" : undefined,
                            left: !props.answerFormat.vertical ? tick + "%" : undefined
                        }}></div>
                    )}
                </div>
                <div className="value max-value">
                    {formatValue(maximumValue.toString())}
                </div>
                <div className="descriptions">
                    <div className="description min-value-description">{props.answerFormat.minimumValueDescription}</div>
                    <div className="description max-value-description">{props.answerFormat.maximumValueDescription}</div>
                    <div style={{ clear: "both" }}></div>
                </div>
            </div>
        </div>
    </div>;
}